import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AppleLogo,
  EnvelopeSimple,
  InstagramLogo,
  FacebookLogo,
  TiktokLogo,
  ShareNetwork,
  ChatCircle,
  ArrowUpRight,
} from "@phosphor-icons/react";
import {
  ASSETS,
  INSTAGRAM_URL,
  FACEBOOK_URL,
  TIKTOK_URL,
  buildAppStoreUrl,
} from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";

const { wordmark: WORDMARK } = ASSETS;

/**
 * /go — Linktree-style link hub. Meant to be dropped in social bios as
 * a single URL that routes visitors to every touchpoint: App Store,
 * newsletter, socials, share, feedback.
 *
 * Every link fires an analytics event tagged with the destination so
 * we can see which bio-link taps convert to installs vs. newsletter
 * signups vs. socials.
 */

// External link tile — opens in a new tab, tracks click.
function ExternalTile({ href, icon, label, sublabel, testId, onClick, primary = false }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      data-testid={testId}
      className={`group flex items-center justify-between gap-4 w-full px-5 py-5 border transition-colors ${
        primary
          ? "border-[#FF2A2A] bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white"
          : "border-white/15 hover:border-[#FF2A2A] hover:bg-white/[0.04] text-white/90 hover:text-white"
      }`}
    >
      <span className="flex items-center gap-4 min-w-0">
        <span
          className={`flex items-center justify-center w-10 h-10 shrink-0 ${
            primary
              ? "bg-white text-[#FF2A2A]"
              : "border border-white/15 text-white/85 group-hover:border-[#FF2A2A] group-hover:text-[#FF2A2A]"
          }`}
        >
          {icon}
        </span>
        <span className="min-w-0">
          <span className="block font-display font-bold text-lg tracking-tight leading-tight truncate">
            {label}
          </span>
          {sublabel ? (
            <span
              className={`block font-mono text-[10px] uppercase tracking-[0.22em] mt-0.5 truncate ${
                primary ? "text-white/80" : "text-white/45"
              }`}
            >
              {sublabel}
            </span>
          ) : null}
        </span>
      </span>
      <ArrowUpRight
        size={20}
        weight="bold"
        className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </a>
  );
}

// Internal Router link — same visual, no target=_blank.
function InternalTile({ to, icon, label, sublabel, testId, onClick }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={testId}
      className="group flex items-center justify-between gap-4 w-full px-5 py-5 border border-white/15 hover:border-[#FF2A2A] hover:bg-white/[0.04] text-white/90 hover:text-white transition-colors"
    >
      <span className="flex items-center gap-4 min-w-0">
        <span className="flex items-center justify-center w-10 h-10 shrink-0 border border-white/15 text-white/85 group-hover:border-[#FF2A2A] group-hover:text-[#FF2A2A]">
          {icon}
        </span>
        <span className="min-w-0">
          <span className="block font-display font-bold text-lg tracking-tight leading-tight truncate">
            {label}
          </span>
          {sublabel ? (
            <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 mt-0.5 truncate">
              {sublabel}
            </span>
          ) : null}
        </span>
      </span>
      <ArrowUpRight
        size={20}
        weight="bold"
        className="shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
      />
    </Link>
  );
}

export default function Go() {
  useEffect(() => {
    document.title = "Stable-ish · Links";
    trackEvent("page_view", "go-page", {});
  }, []);

  const track = (label) => () =>
    trackEvent("go_link_click", "go-page", { destination: label });

  return (
    <main
      className="min-h-screen bg-[#050505] text-white noise flex flex-col"
      data-testid="go-page"
    >
      <div className="w-full max-w-[520px] mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-16 flex-1">
        {/* Header */}
        <div className="text-center">
          <div className="inline-block relative overflow-hidden h-14 w-[220px]">
            <img
              src={WORDMARK}
              alt="Stable-ish"
              draggable="false"
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-none pointer-events-none"
              style={{ height: "320px" }}
            />
          </div>
          <p
            className="mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A]"
            data-testid="go-tagline"
          >
            // for ER + ICU nurses
          </p>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[0.95]">
            The patient is{" "}
            <span className="italic font-light text-white/70">stable</span>
            <span className="text-[#FF2A2A]">-ish.</span>
          </h1>
        </div>

        {/* Links */}
        <div className="mt-10 space-y-3" data-testid="go-links">
          <ExternalTile
            href={INSTAGRAM_URL}
            icon={<InstagramLogo size={20} weight="bold" />}
            label="Instagram"
            sublabel="@_stableish"
            testId="go-link-instagram"
            onClick={track("instagram")}
            primary
          />

          <ExternalTile
            href={buildAppStoreUrl("go_page")}
            icon={<AppleLogo size={20} weight="fill" />}
            label="Download the App"
            sublabel="14 days free · iOS"
            testId="go-link-app-store"
            onClick={track("app_store")}
          />

          <InternalTile
            to="/subscribe?src=go"
            icon={<EnvelopeSimple size={20} weight="bold" />}
            label="Get email updates"
            sublabel="Bedside tools · Android launch"
            testId="go-link-subscribe"
            onClick={track("subscribe")}
          />

          <ExternalTile
            href={FACEBOOK_URL}
            icon={<FacebookLogo size={20} weight="bold" />}
            label="Facebook"
            sublabel="Follow the page"
            testId="go-link-facebook"
            onClick={track("facebook")}
          />

          {TIKTOK_URL ? (
            <ExternalTile
              href={TIKTOK_URL}
              icon={<TiktokLogo size={20} weight="bold" />}
              label="TikTok"
              sublabel="Bedside clips"
              testId="go-link-tiktok"
              onClick={track("tiktok")}
            />
          ) : null}

          <InternalTile
            to="/share"
            icon={<ShareNetwork size={20} weight="bold" />}
            label="Share with a nurse friend"
            sublabel="Pass it along"
            testId="go-link-share"
            onClick={track("share")}
          />

          <InternalTile
            to="/feedback"
            icon={<ChatCircle size={20} weight="bold" />}
            label="Send feedback"
            sublabel="Feature requests + bugs"
            testId="go-link-feedback"
            onClick={track("feedback")}
          />
        </div>

        {/* Compliance footer */}
        <div
          className="mt-14 pt-6 border-t border-[#FF2A2A]/30 text-center"
          data-testid="go-footer"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF2A2A]/80">
            &#9888; for licensed healthcare professionals
          </p>
          <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">
            not a substitute for clinical judgment
          </p>
          <div className="mt-6 flex items-center justify-center gap-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
            <Link
              to="/"
              className="hover:text-[#FF2A2A] transition-colors"
              data-testid="go-home-link"
            >
              stableish.me
            </Link>
            <span className="text-white/20">·</span>
            <Link
              to="/privacy"
              className="hover:text-[#FF2A2A] transition-colors"
              data-testid="go-privacy-link"
            >
              privacy
            </Link>
            <span className="text-white/20">·</span>
            <Link
              to="/terms"
              className="hover:text-[#FF2A2A] transition-colors"
              data-testid="go-terms-link"
            >
              terms
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
